import IntegrahubProductsAPI from '../../api/integrahubProducts';

const firstPresentValue = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== '');

const normalizedRawValue = (item, ...keys) =>
  firstPresentValue(
    ...keys.flatMap(key => [
      item?.[key],
      item?.[String(key).toLowerCase()],
      item?.[String(key).toUpperCase()],
      item?.rawJson?.[key],
      item?.rawJson?.[String(key).toLowerCase()],
      item?.rawJson?.[String(key).toUpperCase()],
    ])
  );

const digitsOnly = value => String(value || '').replace(/\D/g, '');

const normalizeProduct = it => {
  const promoPrice =
    Number(
      it.pricePromo ??
        it.pricepromo ??
        it.PRICEPROMO ??
        (it.rawJson &&
          (it.rawJson.pricePromo ??
            it.rawJson.pricepromo ??
            it.rawJson.PRICEPROMO))
    ) || 0;
  const regularPrice =
    Number(
      it.price ??
        it.PRICE ??
        (it.rawJson && (it.rawJson.price ?? it.rawJson.PRICE))
    ) || 0;
  const ean = firstPresentValue(
    normalizedRawValue(it, 'ean', 'barcode', 'gtin', 'eanCode'),
    normalizedRawValue(it, 'codBarra', 'codigoBarras', 'codigoDeBarras')
  );

  return {
    id: it.productId || it.id || it.product_id,
    name: it.title || it.name || (it.rawJson && it.rawJson.title) || '',
    sku: (it.rawJson && it.rawJson.sku) || it.sku || it.productId || it.id,
    ean: ean ? String(ean) : '',
    price: regularPrice,
    promoPrice,
    availablePrices: null,
    stock: Number(it.stock || (it.rawJson && it.rawJson.stock) || 0),
    brand: it.brand || (it.rawJson && it.rawJson.brand) || null,
    category:
      it.category || it.brand || (it.rawJson && it.rawJson.category) || null,
    raw: it.rawJson || it,
  };
};

const buildAvailablePrices = (regularPrice, promoPrices) => {
  const seen = new Set();
  const result = [];

  const add = (p, label) => {
    const rounded = Math.round(p * 100) / 100;
    if (rounded > 0 && !seen.has(rounded)) {
      seen.add(rounded);
      result.push({ price: rounded, label });
    }
  };

  add(regularPrice, 'Tabela');
  promoPrices.forEach(p => add(p, 'Promoção'));

  return result;
};

const groupByEan = items => {
  const byEan = new Map();
  const noEan = [];

  items.forEach(item => {
    if (!item.ean) {
      noEan.push(item);
      return;
    }
    if (!byEan.has(item.ean)) {
      byEan.set(item.ean, { base: item, promoPrices: [] });
    }
    const entry = byEan.get(item.ean);
    if (item.promoPrice > 0) {
      entry.promoPrices.push(item.promoPrice);
    }
  });

  const grouped = [];
  byEan.forEach(({ base, promoPrices }) => {
    grouped.push({
      ...base,
      availablePrices: buildAvailablePrices(base.price, promoPrices),
    });
  });

  noEan.forEach(item => {
    grouped.push({
      ...item,
      availablePrices: buildAvailablePrices(
        item.price,
        item.promoPrice > 0 ? [item.promoPrice] : []
      ),
    });
  });

  return grouped;
};

const normalizeProducts = rawItems =>
  groupByEan((rawItems || []).map(normalizeProduct));

export const state = {
  items: [],
  pagination: { page: 1, limit: 40, count: 0 },
  uiFlags: {
    isFetching: false,
  },
  error: null,
};

export const getters = {
  allProducts: $state => $state.items,
  productsPagination: $state => $state.pagination,
  productsUIFlags: $state => $state.uiFlags,
};

export const actions = {
  fetch: async ({ commit }, { q, ean, limit = 40, page = 1 } = {}) => {
    commit('SET_UI_FLAG', { isFetching: true });
    commit('SET_ERROR', null);
    try {
      const res = await IntegrahubProductsAPI.search({ q, ean, limit, page });
      const payload = res.data || {};
      const rawItems = payload.items || payload.data || [];
      const finalPagination = payload.pagination || {
        page,
        limit,
        count: payload.count || rawItems.length,
      };
      const normalized = normalizeProducts(rawItems);

      commit('SET_ITEMS', normalized);
      commit('SET_PAGINATION', finalPagination);
    } catch (e) {
      commit('SET_ITEMS', []);
      commit('SET_PAGINATION', { page: 1, limit: 40, count: 0 });
      commit('SET_ERROR', e?.response?.data || { message: e.message });
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },
  fetchByEan: async ({ commit }, { ean, limit = 200, maxPages = 50 } = {}) => {
    commit('SET_UI_FLAG', { isFetching: true });
    commit('SET_ERROR', null);

    try {
      const targetEan = digitsOnly(ean);
      if (!targetEan) {
        commit('SET_ITEMS', []);
        commit('SET_PAGINATION', { page: 1, limit, count: 0 });
        return;
      }

      let page = 1;
      let totalPages = 1;
      const allMatched = [];

      do {
        const res = await IntegrahubProductsAPI.search({
          ean: targetEan,
          limit,
          page,
        });
        const payload = res.data || {};
        const rawItems = payload.items || payload.data || [];
        const normalizedItems = (rawItems || []).map(normalizeProduct);
        const pagination = payload.pagination || {};

        totalPages =
          Number(pagination.total_pages) ||
          Number(
            Math.ceil(
              Number(pagination.count || payload.count || 0) /
                Number(limit || 1)
            )
          ) ||
          1;

        normalizedItems
          .filter(item => digitsOnly(item.ean) === targetEan)
          .forEach(item => allMatched.push(item));

        page += 1;
      } while (page <= totalPages && page <= maxPages);

      const grouped = groupByEan(allMatched);
      commit('SET_ITEMS', grouped);
      commit('SET_PAGINATION', {
        page: 1,
        limit,
        count: grouped.length,
      });
    } catch (e) {
      commit('SET_ITEMS', []);
      commit('SET_PAGINATION', { page: 1, limit, count: 0 });
      commit('SET_ERROR', e?.response?.data || { message: e.message });
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },
};

export const mutations = {
  SET_ITEMS($state, items) {
    $state.items = items;
  },
  SET_PAGINATION($state, pagination) {
    $state.pagination = pagination;
  },
  SET_UI_FLAG($state, uiFlag) {
    $state.uiFlags = { ...$state.uiFlags, ...uiFlag };
  },
  SET_ERROR($state, error) {
    $state.error = error;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
